<template>
  <div id="music">
    <particle-wave :particle-size="9" :number-of-particles="600" :r="146" :g="48" :b="28" />
    <div id="bg-imgs">
      <slick :options="slickOptions" ref="slickRef">
        <div class="slide" v-for="(img, index) in imgs" :key="index">
          <img :src="img.src" alt="XOI" />
        </div>
      </slick>

      <div class="overlay top" />
      <div class="overlay left" />
      <div class="overlay bottom" />
      <div class="overlay right" />
    </div>

    <div class="main-wrapper">
      <div class="container main">
        <p>{{ t('PART_TIME_MUSICIANS') }}</p>
      </div>

      <div class="main-inner-wrapper" ref="wrapperRef">
        <div class="children">
          <div class="container">
            <div class="row">
              <div class="col col-12 col-md-6 col-lg-4">
                <h1>{{ t('LATEST_RELEASE') }}</h1>
                <p v-if="locale === 'vi'">Các bản thu studio mới nhất của XOI. Bạn có thể nghe &amp; theo dõi dự án trân <a :href="LINKS.SPOTIFY" target="_blank" rel="noopener noreferrer">Spotify</a>, <a :href="LINKS.BANDCAMP" target="_blank" rel="noopener noreferrer">Bandcamp</a> và các kênh âm nhạc khác.</p>
                <p v-if="locale === 'en'">XOI's latest studio releases. You can listen &amp; follow this project on <a :href="LINKS.SPOTIFY" target="_blank" rel="noopener noreferrer">Spotify</a>, <a :href="LINKS.BANDCAMP" target="_blank" rel="noopener noreferrer">Bandcamp</a> and other online music channels.</p>

                <div class="bandcamp-holder">
                  <iframe title="Bandcamp" src="https://bandcamp.com/EmbeddedPlayer/album=3508064301/size=large/bgcol=333333/linkcol=e99708/tracklist=false/transparent=true/" seamless />
                  <a href="https://xoimusic.bandcamp.com/album/b-n-ballad-c-a-t-o">Bản Ballad Của Tèo by XOI</a>
                </div>

                <div class="bandcamp-holder">
                  <iframe title="Bandcamp" src="https://bandcamp.com/EmbeddedPlayer/album=534248031/size=large/bgcol=333333/linkcol=e32c14/tracklist=false/transparent=true/" seamless />
                  <a href="https://xoimusic.bandcamp.com/album/the-one-who-holds-the-flame">The One Who Hold the Flame by XOI</a>
                </div>
              </div>

              <div class="col col-12 col-md-6 col-lg-8 mv">
                <h1>{{ t('VIDEO') }}</h1>
                <p v-if="locale === 'vi'">Các sản phẩm MV của XOI. Xem thêm &amp; theo dõi dự án trên <a :href="LINKS.YOUTUBE" target="_blank" rel="noopener noreferrer">Youtube</a>.</p>
                <p v-if="locale === 'en'">XOI's music video productions. Watch more &amp; follow this project on <a :href="LINKS.YOUTUBE" target="_blank" rel="noopener noreferrer">Youtube</a>.</p>

                <h3>{{ mvTitle }}</h3>
                <div class="video-holder">
                  <video controls :poster="mvThumb">
                    <source :src="mv" type="video/mp4">
                  </video>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col col-12">
                <h1>{{ t('ABOUT_XOI') }}</h1>

                <p>{{ t('PART_TIME_MUSICIANS') }}</p>

                <div class="socials">
                  <p>{{ t('FOLLOW_XOI_ON') }}:</p>
                  <!-- eslint-disable vuejs-accessibility/anchor-has-content -->
                  <a :href="LINKS.SPOTIFY" target="_blank" rel="noopener noreferrer"><font-awesome-icon :icon="faSpotify" /></a>
                  <a :href="LINKS.BANDCAMP" target="_blank" rel="noopener noreferrer"><font-awesome-icon :icon="faBandcamp" /></a>
                  <a :href="LINKS.YOUTUBE" target="_blank" rel="noopener noreferrer"><font-awesome-icon :icon="faYoutube" /></a>
                  <a :href="LINKS.INSTAGRAM" target="_blank" rel="noopener noreferrer"><font-awesome-icon :icon="faInstagram" /></a>
                  <a :href="LINKS.FACEBOOK" target="_blank" rel="noopener noreferrer"><font-awesome-icon :icon="faFacebook" /></a>
                  <!-- eslint-enable -->
                </div>

                <p><router-link :to="PATHS.SHOP">{{ t('BUY_OUR_PRODUCTS_HERE') }}</router-link></p>
              </div>
            </div>
          </div>
        </div>

        <p class="footer">{{ t('POWERED_BY_XOI') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { watch, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMeta } from 'vue-meta';
import $ from 'jquery';
import { TweenMax, Power1 } from 'gsap';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faFacebook, faInstagram, faYoutube, faSpotify, faBandcamp } from '@fortawesome/free-brands-svg-icons';

import { LINKS } from '@/consts';
import PATHS from '@/consts/paths';

import ParticleWave from '@/components/ParticleWave';
import Slick from '@/components/Slick';

import coverImg from '@/assets/imgs/xoi-music-2025.jpg';

import img020 from '@/assets/imgs/photoshoot-2025/020.jpg';
import img021 from '@/assets/imgs/photoshoot-2025/021.jpg';
import img022 from '@/assets/imgs/photoshoot-2025/022.jpg';
import img023 from '@/assets/imgs/photoshoot-2025/023.jpg';
import img024 from '@/assets/imgs/photoshoot-2025/024.jpg';
import img025 from '@/assets/imgs/photoshoot-2025/025.jpg';
import img026 from '@/assets/imgs/photoshoot-2025/026.jpg';
import img027 from '@/assets/imgs/photoshoot-2025/027.jpg';
import img028 from '@/assets/imgs/photoshoot-2025/028.jpg';
import img029 from '@/assets/imgs/photoshoot-2025/029.jpg';
import img030 from '@/assets/imgs/photoshoot-2025/030.jpg';

import asbnthumb from '@/assets/imgs/the-light-of-day-mv-thumbnail-min.jpg';

const slickOptions = {
  arrows: false,
  autoplay: true,
  infinite: true,
  speed: 500,
  fade: true,
  cssEase: 'linear'
};

const imgs = [
  {
    src: img020
  },
  {
    src: img021
  },
  {
    src: img022
  },
  {
    src: img023
  },
  {
    src: img024
  },
  {
    src: img025
  },
  {
    src: img026
  },
  {
    src: img027
  },
  {
    src: img028
  },
  {
    src: img029
  },
  {
    src: img030
  },
];

const mv = 'https://assets.xoi.vn/xoi-the-light-of-day.mp4';
const mvThumb = asbnthumb;
const mvTitle = 'XOI - The Light of Day (Official Music Video)';

export default {
  name: 'Music',
  components: {
    ParticleWave,
    Slick,
    FontAwesomeIcon
  },
  setup() {
    const { t, locale } = useI18n();

    const title = t('MUSIC');
    const description = t('PART_TIME_MUSICIANS');

    const { meta } = useMeta({
      title,
      description,
      og: {
        title: `${title} | XOI`,
        description,
        image: [coverImg]
      },
    });

    watch(locale, () => {
      meta.title = t('MUSIC');
    });

    const wrapperRef = ref(null);
    const slickRef = ref(null);
    const freeze = ref(false);

    const scrollChildren = () => {
      if (!wrapperRef.value || !wrapperRef.value) return;
      if (!slickRef.value || !slickRef.value) return;

      const top = $(window).scrollTop();

      if (top > window.innerHeight) {
        slickRef.value.pause();
        freeze.value = true;
      } else {
        slickRef.value.play();
        freeze.value = false;
      }

      TweenMax.to(wrapperRef.value, .8, {
        ease: Power1.easeOut,
        y: -top
      });
    };

    const offsetScroll = () => {
      const offset = 110;

      TweenMax.set(wrapperRef.value, {
        y: 0
      });

      TweenMax.set('body', {
        height: wrapperRef.value.scrollHeight + offset
      });
    };

    onMounted(() => {
      $(window).on('scroll', scrollChildren);
      $(window).on('resize', offsetScroll);
      offsetScroll();
    });

    onUnmounted(() => {
      $(window).off('scroll', scrollChildren);
      $(window).off('resize', offsetScroll);
      TweenMax.set('body', {
        height: null
      });
    });

    return {
      t,
      locale,
      slickOptions,
      imgs,
      wrapperRef,
      slickRef,
      mv,
      mvThumb,
      mvTitle,
      faFacebook,
      faInstagram,
      faYoutube,
      faSpotify,
      faBandcamp,
      LINKS,
      PATHS
    };
  }
};
</script>

<style lang="scss">
@use "~@/assets/scss/colors" as *;
@use "~@/assets/scss/dimens" as *;

#music {
  width: 100%;
  min-height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
  background-color: $black;

  a {
    font-weight: bold;
  }

  #particle-wave {
    position: fixed;
    top: $headerHeight;
    left: 0;
    width: 100%;
    height: 100vh;

    canvas {
      background-color: $black;
    }
  }

  #bg-imgs {
    height: 100vh;
    overflow: hidden;
    opacity: .5;
    position: relative;

    .slider {
      height: 100%;

      .item {
        height: 100%;
      }
    }

    .slick-list {
      height: 100%;
    }

    .slick-track {
      height: 100%;
    }

    .slick-slide {
      height: auto;
    }

    .slide {
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
    }

    >.overlay {
      background-color: $sienna;
      mix-blend-mode: hard-light;
      position: absolute;

      &.top {
        width: 100%;
        height: 20px;
        top: 0;
        left: 0;
      }

      &.bottom {
        width: 100%;
        height: 20px;
        bottom: 0;
        left: 0;
      }

      &.left {
        width: 20px;
        height: calc(100% - 40px);
        top: 20px;
        left: 0;
      }

      &.right {
        width: 20px;
        height: calc(100% - 40px);
        top: 20px;
        right: 0;
      }
    }
  }

  .main-wrapper {
    position: absolute;
    top: 90px;
    left: 20px;
    width: calc(100% - 40px);
    height: calc(100% - 110px);
    overflow: hidden;

    .main {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translateY(-50%) translateX(-50%);
      color: $white;
      text-align: center;

      p {
        font-size: 24px;
      }
    }

    .main-inner-wrapper {
      padding-top: calc(100vh - 40px);

      .children {
        position: relative;
        z-index: 9;
        padding: 80px 0;
        text-align: left;
        background-color: $white;

        .container {
          max-width: 960px;
        }

        .col {
          margin-bottom: 50px;
        }

        .bandcamp-holder {
          width: 100%;
          padding-bottom: 120%;
          margin-bottom: 15px;
          position: relative;

          iframe {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 4px;
            border: 0;
          }
        }

        .video-holder {
          width: 100%;
          padding-bottom: 58.16%;
          position: relative;

          video {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 4px;
            border: 0;

            &:active {
              border: none;
            }
          }
        }

        .mv {
          h3 {
            font-weight: bold;
            margin-bottom: 10px;
            margin-top: 30px;
            font-size: 18px;
          }
        }

        .socials {
          svg {
            margin-right: 10px;
            font-size: 24px;
            margin-bottom: 10px;
          }
        }
      }

      .footer {
        text-align: center;
        color: $white;
        font-size: 12px;
        padding: 20px 0;
      }
    }
  }
}
</style>
